/** Query Function Types */

import { EntityId, EntityState } from '@reduxjs/toolkit';

import { HumanizeTierReturn } from '@/utils/humanizeTier';

/** RTK Query Responses & Args */

// getAnnualPasses
export type GetAnnualPassesApiResponse = EntityState<PassportEntity, EntityId>;
export type GetAnnualPassesApiArg = void;

// getRewards
export type GetRewardsApiResponse = EntityState<MemberRewardInfoEntity, EntityId>;
export type GetRewardsApiArg = string;

/** type/class so that otter has an easy way to retrieve rewards already filtered */
export type CategorizedRewards = {
  availableNonInParkRewards: MemberRewardInfoEntity[];
  inParkRewards: MemberRewardInfoEntity[];
  allRewards: MemberRewardInfoEntity[];
};
export type GetAllRewardsForActivePassesApiResponse = MemberRewardInfoEntity[];
export type GetAllRewardsForActivePassesApiArg = void;

/** Entity Adapter Entities */

export type PassportEntity = Partial<Pass> & {
  passmemberFullName?: string | null;
  tierHumanized?: HumanizeTierReturn | null;
};

export type MemberRewardInfoEntity = Partial<MemberRewardInfo> & {
  id: string | null;
  associatedBarcode: string | null;
  filterTags: string[];
  firstName?: string;
  lastName?: string;
  tier?: PassTier;
};

/** Coral Responses */

export interface AnnualPassResponse {
  annualPasses: Array<Pass>;
}

export interface Pass {
  productName?: string | null;
  itemClass?: string | null;
  status?: PassportStatus | null;
  expirationDate?: string | null;
  purchaseDate?: string | null;
  renewalCode?: string | null;
  tier?: PassTier | null;
  parks?: Array<number> | null;
  passmemberFirstName?: string | null;
  passmemberLastName?: string | null;
  passmemberBarcode?: string | null;
  isOnHold?: boolean;
}

export enum PassTier {
  DEFAULT = 0,
  SINGLE_DAY_TICKET = 1,
  MULTI_DAY_TICKET = 2,
  BRONZE_PASS = 3,
  SILVER_PASS = 4,
  GOLD_PASS = 5,
  PLATINUM_PASS = 6,
  SPECIAL_EVENT = 7,
  GIFT_CERTIFICATE = 8,
  OTHER = 9,
  DINE_CERTIFICATE = 11,
  RESERVATION = 13,
  NON_ADMISSION = 14,
  FUN_CARD = 15,
  SEASON_PASS = 16,
  TRANSFERRABLE_PASS = 17,
  COMP_PASS = 18,
}

export enum PassportStatus {
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  EXPIRING_SOON = 'EXPIRING SOON',
}

export interface GetMemberRewardInfoResponse {
  isSuccess?: boolean;
  messageType?: string | null;
  messageSeverity?: number;
  messageSeverityDictionary?: Array<string> | null;
  messageCode?: number;
  messageCodeDictionary?: Array<string> | null;
  messageException?: string | null;
  messageCustomValuesPiped?: string | null;
  type?: string | null;
  title?: string | null;
  detail?: string | null;
  instance?: string | null;
  status?: number;
  memberRewardInfo?: Array<MemberRewardInfo> | null;
}

export interface MemberRewardInfo {
  account?: number;
  accountType?: string | null;

  /** URL path to the reward's image asset */
  image?: string | null;

  /** Display name of the reward */
  name?: string | null;

  /** Unique identifier code for the reward */
  rewardCode?: string | null;

  /** Detailed description of what the reward offers */
  rewardDescription?: string | null;

  /** Brief summary of the reward */
  shortDescription?: string | null;
  redemptionPlu?: number;
  sellingGroup?: number;
  sellingGroupSitecoreId?: string;
  quantity?: number;
  remaining?: number;
  redemptions?: Array<MemberRewardRedemption> | null;

  /** Tags to help identify/filter rewards */
  filterTags: string[];

  /** List of park Ids where this reward can be used */
  parkIds: string[];

  /** The original price, to calculate savings and show strikethrough */
  wasPrice?: number;

  /** The price that will be displayed at the gate*/
  gatePrice?: number;

  /** The price that is current, usually 0 for rewards*/
  pricesStartAt?: number;
}

export interface MemberRewardRedemption {
  transactionDate?: string;
  transactionCode?: string | null;
  salesChannel?: string | null;
  transactionType?: string | null;
  parkID?: number;
  barcode?: string | null;
}
